import { debugLog, errorLog } from '../utils/environment';

/**
 * Pi Ads SDK Wrapper Service
 * Pi Network 광고 기능을 캡슐화하여 제공
 * @see https://github.com/pi-apps/pi-platform-docs/blob/master/ads.md
 * @see https://github.com/pi-apps/pi-platform-docs/blob/master/SDK_reference.md#ads
 */
const PiAdService = {
    /**
     * Pi Browser가 광고 네트워크(ad_network)를 지원하는지 확인
     * ADS_NOT_SUPPORTED = native features list에 'ad_network'가 없음 (구버전 브라우저)
     * @returns {Promise<boolean>}
     */
    isAdNetworkSupported: async () => {
        if (!window.Pi || typeof window.Pi.nativeFeaturesList !== 'function') {
            return false;
        }
        try {
            const list = await window.Pi.nativeFeaturesList();
            const supported = Array.isArray(list) && list.includes('ad_network');
            debugLog('[PiAdService] nativeFeaturesList ad_network:', supported);
            return supported;
        } catch (e) {
            errorLog('[PiAdService] nativeFeaturesList 실패:', e);
            return false;
        }
    },

    /**
     * 보상형 광고 표시
     * - 승인 전에도 광고 표시는 가능하며, adId가 없을 수 있음(SDK 문서)
     * - adId가 없는 경우 strict 모드 여부에 따라 서버에서 처리 전략을 결정
     * @param {string} adUnitId - 광고 단위 ID (star/hint/heart 등, 로깅/구분용)
     * @returns {Promise<string|null>} adId - 검증용 (strict=false일 때는 null 허용)
     */
    showRewardedAd: async (adUnitId) => {
        debugLog('[PiAdService] showRewardedAd 요청:', adUnitId);

        if (!window.Pi || !window.Pi.Ads) {
            errorLog('[PiAdService] Pi SDK 또는 Ads 모듈을 찾을 수 없습니다.');
            throw new Error('PI_ADS_NOT_LOADED');
        }

        try {
            // 1. Prerequisites: ad_network 지원 여부 사전 체크 (ads.md)
            const supported = await PiAdService.isAdNetworkSupported();
            if (!supported) {
                throw new Error('AD_SHOW_FAILED: ADS_NOT_SUPPORTED');
            }

            // 2. 광고 표시 (SDK: showAd(adType: "rewarded"))
            const showAdResponse = await window.Pi.Ads.showAd("rewarded");

            debugLog('[PiAdService] showAd 결과:', showAdResponse);

            if (showAdResponse.result === "AD_REWARDED") {
                // 미승인 앱은 adId가 없을 수 있음 → strict 모드 여부에 따라 서버에서 처리
                if (!showAdResponse.adId) {
                    debugLog('[PiAdService] AD_REWARDED 응답이지만 adId 없음 - 서버에서 완화/엄격 모드 처리 예정');
                    return null;
                }
                return showAdResponse.adId;
            }
            if (showAdResponse.result === "AD_CLOSED") {
                throw new Error('AD_CLOSED_BY_USER');
            }
            // ADS_NOT_SUPPORTED, AD_NOT_AVAILABLE 등
            throw new Error(`AD_SHOW_FAILED: ${showAdResponse.result}`);
        } catch (e) {
            errorLog('[PiAdService] 광고 표시 중 예외 발생:', e);
            throw e;
        }
    },

    // Future: Interstitial supports
};

export default PiAdService;
